import {
  type PathLookupEventsSummary,
  type PathLookupGlobalEvent,
  type ReportSeverity,
  pathLookupEventTopics,
  reportSeverities,
} from '@ipfabric/utils';
import { array, readonlyArray, record } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';

export interface SeverityWithCount {
  count: number;
  severity: ReportSeverity;
}

const LOWEST_SEVERITY: ReportSeverity = Math.min(
  ...reportSeverities,
) as ReportSeverity;
const createInitialSeverityWithCount = (): SeverityWithCount => ({
  severity: LOWEST_SEVERITY,
  count: 0,
});

/**
 * Get highest occurred severity with sum of all counts of that severity
 */
const mergeSeverityWithCount = (
  severities: SeverityWithCount[],
): SeverityWithCount =>
  severities.reduce((prev, current) => {
    if (current.count === 0) {
      return prev;
    }
    if (prev.severity === current.severity) {
      return {
        severity: current.severity,
        count: prev.count + current.count,
      };
    }
    if (prev.severity < current.severity) {
      return current;
    }
    return prev;
  }, createInitialSeverityWithCount());

// FIXME string should be ReportSeverity, but that is a number and that shouldn't be
// object key anyway, because Object.keys() will always return it as a string.
// We should probably fix this everywhere where ReportSeverity is used as a key?
const getTopicSeverities = record.collect<string, number, SeverityWithCount>(
  (severity, count) => ({
    count,
    severity: Number.parseInt(severity, 10) as ReportSeverity,
  }),
);

const getTopicsSeverities = (
  topics: PathLookupEventsSummary['topics'],
): readonly SeverityWithCount[] =>
  pipe(
    pathLookupEventTopics,
    readonlyArray.map((topic) => getTopicSeverities(topics[topic])),
    readonlyArray.flatten,
  );

const getGlobalSeverities = array.map<PathLookupGlobalEvent, SeverityWithCount>(
  (event) => ({ severity: event.severity, count: 1 }),
);

export const getPathLookupEventsSeverity = (
  eventsSummary: PathLookupEventsSummary,
): SeverityWithCount =>
  mergeSeverityWithCount([
    ...getGlobalSeverities(eventsSummary.global),
    ...getTopicsSeverities(eventsSummary.topics),
  ]);
